/**
 * Chat SignalR Event Management Hook - Phase 3
 * 
 * Extracts all SignalR event handling logic from useChat.final
 * Provides clean separation of SignalR concerns with stable event handlers
 * 
 * Features:
 * - Focused SignalR event management for chat functionality
 * - Stable refs for consistent event handler behavior
 * - Intelligent deduplication trust (delegates to SignalRManager)
 * - Clean callback interface for UI state updates
 * - Session context management integration
 * - Local streaming simulation when backend sends complete message without chunks
 */

import { useCallback, useRef, useEffect } from 'react';
import { useUnifiedSignalR } from '../contexts/UnifiedSignalRContext';
import type { SignalRStreamEvent, SignalRCompleteEvent, SignalRStatusEvent, SignalRActiveRunsBlockedEvent, SignalRRunsStatusEvent } from '../types/chat';

// Configuration for local streaming simulation
const STREAM_SIMULATION_CONFIG = {
  chunkSize: 3,           // Words per chunk
  chunkDelayMs: 50,       // Milliseconds between chunks
  minContentLength: 20,   // Minimum content length to simulate streaming
};

interface MessageOperations {
  findMessage: (messageId: string, role?: 'user' | 'assistant') => any;
  createAssistantPlaceholder: (messageId: string, sessionId: string, metadata?: any) => void;
  updateAssistantMessage: (messageId: string, chunk: string, isComplete: boolean, fullContent?: any, metadata?: any) => void;
}

interface ChatSignalRHandlers {
  onStreamingUpdate?: (messageId: string, isComplete: boolean, metadata?: any) => void;
  onStatusUpdate?: (status: string | null, metadata?: any) => void;
  onSessionUpdate?: (data: any) => void;
  onMultiMessageRunUpdate?: (runId: string, isLastMessage: boolean, messageIndex: number) => void;
  onActiveRunsBlocked?: (data: SignalRActiveRunsBlockedEvent) => void;
  onRunsStatus?: (data: SignalRRunsStatusEvent) => void;
}

export const useChatSignalR = (
  currentSessionId: string | null,
  messageOps: MessageOperations,
  handlers: ChatSignalRHandlers = {}
) => {
  const { isConnected, subscribe, setSessionContext, clearSessionContext } = useUnifiedSignalR();
  
  // Refs for stable access in event handlers
  const messageOpsRef = useRef(messageOps);
  const currentSessionIdRef = useRef(currentSessionId);
  const handlersRef = useRef(handlers);
  
  // Track which messages have received stream chunks (to detect when simulation is needed)
  const streamedMessagesRef = useRef(new Set<string>());
  
  // Track active streaming simulations to allow cleanup
  const activeSimulationsRef = useRef(new Map<string, { cancelled: boolean }>());
  
  // Update refs when values change
  useEffect(() => {
    messageOpsRef.current = messageOps;
    currentSessionIdRef.current = currentSessionId;
    handlersRef.current = handlers;
  }, [messageOps, currentSessionId, handlers]);

  /**
   * Extract text content from fullContent structure
   * Handles various backend response formats
   */
  const extractTextFromFullContent = useCallback((fullContent: any): string => {
    if (!fullContent) return '';
    
    if (typeof fullContent === 'string') {
      return fullContent;
    }
    
    if (Array.isArray(fullContent)) {
      // Handle array format: [{text: {value: "..."}}] or [{content: "..."}]
      const firstContent = fullContent[0];
      return firstContent?.text?.value || firstContent?.content || '';
    }
    
    if (fullContent?.text?.value) {
      // Handle object format: {text: {value: "..."}}
      return fullContent.text.value;
    }
    
    return '';
  }, []);

  /**
   * Simulate streaming for a complete message that arrived without prior chunks
   * Breaks content into word chunks and delivers them with delays
   */
  const simulateStreaming = useCallback(async (
    messageId: string,
    content: string,
    fullContent: any,
    metadata: any,
    sessionId: string
  ) => {
    const currentOps = messageOpsRef.current;
    const currentHandlers = handlersRef.current;
    
    if (!currentOps || !content || content.length < STREAM_SIMULATION_CONFIG.minContentLength) {
      // Content too short, just show it immediately
      currentOps?.updateAssistantMessage(messageId, content, true, fullContent, metadata);
      currentHandlers.onStreamingUpdate?.(messageId, true, metadata);
      return;
    }

    // Create simulation tracking object
    const simulation = { cancelled: false };
    activeSimulationsRef.current.set(messageId, simulation);
    
    // Split content into word chunks
    const words = content.split(/(\s+)/); // Keep whitespace as separate elements
    const chunks: string[] = [];
    let currentChunk = '';
    let wordCount = 0;
    
    for (const word of words) {
      currentChunk += word;
      if (!/^\s+$/.test(word)) { // Only count actual words, not whitespace
        wordCount++;
      }
      
      if (wordCount >= STREAM_SIMULATION_CONFIG.chunkSize) {
        chunks.push(currentChunk);
        currentChunk = '';
        wordCount = 0;
      }
    }
    
    // Don't forget the last chunk
    if (currentChunk) {
      chunks.push(currentChunk);
    }
    
    // Create placeholder for the message
    const existingMessage = currentOps.findMessage(messageId, 'assistant');
    if (!existingMessage) {
      currentOps.createAssistantPlaceholder(messageId, sessionId, metadata);
    }
    
    // Notify UI that streaming has started
    currentHandlers.onStreamingUpdate?.(messageId, false, metadata);
    
    // Stream chunks with delays
    for (let i = 0; i < chunks.length; i++) {
      // Check if simulation was cancelled (e.g., user navigated away)
      if (simulation.cancelled) {
        break;
      }
      
      const isLastChunk = i === chunks.length - 1;
      
      currentOps.updateAssistantMessage(
        messageId,
        chunks[i],
        isLastChunk,
        isLastChunk ? fullContent : undefined,
        metadata
      );
      
      if (!isLastChunk) {
        await new Promise(resolve => setTimeout(resolve, STREAM_SIMULATION_CONFIG.chunkDelayMs));
      }
    }
    
    // Clean up simulation tracking
    activeSimulationsRef.current.delete(messageId);
    
    // Notify UI that streaming is complete
    if (!simulation.cancelled) {
      currentHandlers.onStreamingUpdate?.(messageId, true, metadata);
      
      // Handle multi-message run completion
      if (metadata && currentHandlers.onMultiMessageRunUpdate) {
        currentHandlers.onMultiMessageRunUpdate(
          metadata.runId,
          metadata.isLastMessage,
          metadata.messageIndex
        );
      }
      
      // Clear status message after completion
      if (currentHandlers.onStatusUpdate) {
        if (!metadata || metadata.isLastMessage) {
          currentHandlers.onStatusUpdate(null, metadata);
        }
      }
    }
  }, []);

    // Stream handler - updates message content as it streams in
  const handleStream = useCallback((data: SignalRStreamEvent) => {
    if (!data?.messageId || data?.chunk === undefined) {
      return;
    }
    
    const currentOps = messageOpsRef.current;
    const sessionId = currentSessionIdRef.current;
    
    if (!currentOps || !sessionId) {
      return;
    }
    
    // Mark this message as having received stream chunks
    streamedMessagesRef.current.add(data.messageId);
    
    // Trust SignalRManager's deduplication - just process the event
    const existingMessage = currentOps.findMessage(data.messageId, 'assistant');
    
    if (!existingMessage) {
      // Create placeholder when we receive the first stream chunk
      // Include multi-message metadata for proper sequencing
      currentOps.createAssistantPlaceholder(data.messageId, sessionId, data.metadata);
    }
    
    // Update message with streaming content and metadata
    currentOps.updateAssistantMessage(
      data.messageId, 
      data.chunk, 
      data.isComplete || false, 
      data.fullContent,
      data.metadata
    );
    
    // Notify UI of streaming state changes with metadata
    const currentHandlers = handlersRef.current;
    if (currentHandlers.onStreamingUpdate) {
      currentHandlers.onStreamingUpdate(data.messageId, data.isComplete || false, data.metadata);
    }
    
    // Handle multi-message run updates
    if (data.metadata && currentHandlers.onMultiMessageRunUpdate) {
      currentHandlers.onMultiMessageRunUpdate(
        data.metadata.runId,
        data.metadata.isLastMessage,
        data.metadata.messageIndex
      );
    }
  }, []);

  // Complete handler - finalizes streaming messages
  const handleComplete = useCallback((data: SignalRCompleteEvent) => {
    if (!data?.messageId) {
      return;
    }
    
    const currentOps = messageOpsRef.current;
    const sessionId = currentSessionIdRef.current;
    
    if (!currentOps) {
      return;
    }
    
    // Check if this message received any stream chunks
    const hasReceivedChunks = streamedMessagesRef.current.has(data.messageId);
    
    // Skip simulation if message was already streamed during monitoring (backend flag)
    // or if we tracked stream chunks for this message
    const skipSimulation = hasReceivedChunks || data.metadata?.alreadyStreamedDuringMonitoring;
    
    if (!skipSimulation) {
      // No stream chunks received - simulate streaming locally
      // Extract content from fullContent or content field
      const content = extractTextFromFullContent(data.fullContent) || data.content || '';
      
      if (content && sessionId) {
        // Clean up tracking for this message
        streamedMessagesRef.current.delete(data.messageId);
        
        // Start local streaming simulation
        simulateStreaming(
          data.messageId,
          content,
          data.fullContent,
          data.metadata,
          sessionId
        );
        return; // simulateStreaming handles all completion logic
      }
    }
    
    // Clean up tracking for this message
    streamedMessagesRef.current.delete(data.messageId);
    
    // Normal flow: message was streamed, just mark as complete
    currentOps.updateAssistantMessage(
      data.messageId, 
      '', 
      true, 
      data.fullContent,
      data.metadata
    );
    
    // Notify UI that streaming is complete with metadata
    const currentHandlers = handlersRef.current;
    if (currentHandlers.onStreamingUpdate) {
      currentHandlers.onStreamingUpdate(data.messageId, true, data.metadata);
    }
    
    // Handle multi-message run completion
    if (data.metadata && currentHandlers.onMultiMessageRunUpdate) {
      currentHandlers.onMultiMessageRunUpdate(
        data.metadata.runId,
        data.metadata.isLastMessage,
        data.metadata.messageIndex
      );
    }
    
    // Clear status message after completion
    // For single messages (no metadata) or last message in multi-message run
    if (currentHandlers.onStatusUpdate) {
      if (!data.metadata || data.metadata.isLastMessage) {
        currentHandlers.onStatusUpdate(null, data.metadata);
      }
    }
  }, [extractTextFromFullContent, simulateStreaming]);

  // Status handler - updates typing indicators and status messages
  const handleStatus = useCallback((data: SignalRStatusEvent) => {
    if (!data?.status || !data?.messageId) {
      return;
    }
    
    // Update status message for UI display with metadata
    const currentHandlers = handlersRef.current;
    if (currentHandlers.onStatusUpdate) {
      currentHandlers.onStatusUpdate(data.status, data.metadata);
    }
    
    // ✅ REMOVED: Don't manage streaming state from status events
    // Status events should only update the status message display
    // Streaming state should only be managed by stream/complete events
  }, []);

  // Session update handler - delegates to session management
  const handleSessionUpdate = useCallback((data: any) => {
    if (!data?.sessionId) {
      return;
    }
    
    // Use the session hook's update handler
    const currentHandlers = handlersRef.current;
    if (currentHandlers.onSessionUpdate) {
      currentHandlers.onSessionUpdate(data);
    }
  }, []);

  // Active runs blocked handler - notifies when message is blocked due to active AI processing
  const handleActiveRunsBlocked = useCallback((data: SignalRActiveRunsBlockedEvent) => {
    if (!data?.sessionId) {
      return;
    }
    
    const currentHandlers = handlersRef.current;
    if (currentHandlers.onActiveRunsBlocked) {
      currentHandlers.onActiveRunsBlocked(data);
    }
  }, []);

  // Runs status handler - notifies when runs are cancelled or status changes
  const handleRunsStatus = useCallback((data: SignalRRunsStatusEvent) => {
    if (!data?.type) {
      return;
    }
    
    const currentHandlers = handlersRef.current;
    if (currentHandlers.onRunsStatus) {
      currentHandlers.onRunsStatus(data);
    }
  }, []);

  // Set up SignalR subscriptions
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    // Subscribe to the events we need for chat UI
    const unsubscribeStream = subscribe('stream', handleStream);
    const unsubscribeComplete = subscribe('complete', handleComplete);
    const unsubscribeStatus = subscribe('status', handleStatus);
    const unsubscribeSessionUpdate = subscribe('updateSession', handleSessionUpdate);
    const unsubscribeActiveRunsBlocked = subscribe('activeRunsBlocked', handleActiveRunsBlocked);
    const unsubscribeRunsStatus = subscribe('runsStatus', handleRunsStatus);

    return () => {
      unsubscribeStream();
      unsubscribeComplete();
      unsubscribeStatus();
      unsubscribeSessionUpdate();
      unsubscribeActiveRunsBlocked();
      unsubscribeRunsStatus();
    };
  }, [isConnected, subscribe, handleStream, handleComplete, handleStatus, handleSessionUpdate, handleActiveRunsBlocked, handleRunsStatus]);

  // Cleanup active simulations when session changes or component unmounts
  useEffect(() => {
    // Capture ref values at effect setup time for cleanup
    const activeSimulations = activeSimulationsRef.current;
    const streamedMessages = streamedMessagesRef.current;
    
    return () => {
      // Cancel all active streaming simulations
      activeSimulations.forEach(simulation => {
        simulation.cancelled = true;
      });
      activeSimulations.clear();
      
      // Clear streamed messages tracking
      streamedMessages.clear();
    };
  }, [currentSessionId]);

  // Context management functions
  const updateSessionContext = useCallback((sessionId: string) => {
    setSessionContext(sessionId);
  }, [setSessionContext]);

  const clearContext = useCallback(() => {
    clearSessionContext();
  }, [clearSessionContext]);

  return {
    isConnected,
    updateSessionContext,
    clearContext
  };
};
